import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';

import ItemTdeTable from './ItemTdeTable';

import { Paginator } from '@/components/Paginator';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import {
  DataTableType,
  EscuelaTO,
  PosicionParticipanteMiEscuelaTO,
  ResponseTdeRankingData,
  UsuarioTO,
} from '@/types/tde';
import { FADE_IN_OUT } from '@/utils';

const RankingTable = ({
  dataTable,
  activeTab,
  schooldata,
  tabTable,
}: {
  dataTable: DataTableType;
  activeTab: 'studentsData' | 'schoolsData' | 'mySchoolsData';
  schooldata: ResponseTdeRankingData;
  tabTable: number;
}) => {
  const { isDesktop, isMobile } = useIsDesktop();
  const [dataVersion, setDataVersion] = useState(0);
  const [activeData, setActiveData] = useState<
    UsuarioTO[] | EscuelaTO[] | PosicionParticipanteMiEscuelaTO[]
  >([]);
  const [contentCurrentPage, setContentCurrentPage] = useState<
    UsuarioTO[] | EscuelaTO[] | PosicionParticipanteMiEscuelaTO[]
  >([]);

  const getActiveData = useCallback(() => {
    switch (activeTab) {
      case 'studentsData':
        return dataTable.studentsData;
      case 'schoolsData':
        return dataTable.schoolsData;
      case 'mySchoolsData':
        return tabTable === 0
          ? dataTable.mySchoolsData
          : dataTable.mySchoolDataGeneral;
      default:
        return [];
    }
  }, [activeTab, dataTable]);

  const initState = useCallback(() => {
    const data = getActiveData();

    // Add 'puesto' key for PosicionParticipanteMiEscuelaTO
    const processedData =
      activeTab === 'mySchoolsData'
        ? (data as PosicionParticipanteMiEscuelaTO[]).map((item, index) => ({
            ...item,
            puesto: String(index + 1),
          }))
        : data;

    setActiveData(processedData);
    if (processedData.length > 0 && isDesktop) {
      setContentCurrentPage(processedData.slice(0, 10));
    } else {
      setContentCurrentPage(processedData);
    }
  }, [getActiveData, isDesktop, activeTab]);

  // Resetear paginación cuando cambia el tab O los datos
  useEffect(() => {
    const data = getActiveData();
    const processedData =
      activeTab === 'mySchoolsData'
        ? (data as PosicionParticipanteMiEscuelaTO[]).map((item, index) => ({
            ...item,
            puesto: String(index + 1),
          }))
        : data;

    setContentCurrentPage(
      processedData.slice(0, isDesktop ? 10 : processedData.length)
    );
    setDataVersion((prev) => prev + 1); // Actualizar versión de datos
  }, [activeTab, isDesktop, getActiveData]);

  useEffect(() => {
    initState();

    return () => {
      setContentCurrentPage([]);
      setActiveData([]);
    };
  }, [initState]);

  const handleContentCurrentPage = (
    dts: UsuarioTO[] | EscuelaTO[] | PosicionParticipanteMiEscuelaTO[]
  ) => {
    dts && setContentCurrentPage(dts);
  };

  return (
    <div className="w-[calc(100%+2rem)] -left-4 relative lg:left-auto lg:w-auto">
      <div className="flex text-gdt-gray-4 font-bold text-xs lg:text-sm h-10 mt-4 lg:mt-3 items-center px-7 bg-gdt-gray-7 lg:bg-white">
        <p className="w-12 lg:w-[68px]">{isDesktop ? 'Posición' : 'Pos'}</p>
        <p className="flex-1">
          {activeTab === 'schoolsData' ? 'Escuela' : 'DT'}
        </p>
        <p className="w-[62px] text-right lg:w-[113px] lg:text-center">
          Puntos
        </p>
      </div>
      {!activeData || activeData.length === 0 ? (
        <div className="h-[350px]" />
      ) : null}
      {activeData && activeData.length > 0 && (
        <motion.div animate={'show'} initial={'hide'} variants={FADE_IN_OUT}>
          <>
            <div className="lg:min-h-[400px] mt-6 rounded-lg bg-white">
              {isDesktop &&
                contentCurrentPage.map(
                  (
                    item:
                      | EscuelaTO
                      | UsuarioTO
                      | PosicionParticipanteMiEscuelaTO,
                    index: number
                  ) => {
                    return (
                      <ItemTdeTable
                        key={`${index}-${Math.random()
                          .toString(36)
                          .substring(2, 15)}`}
                        activeTab={activeTab}
                        index={index}
                        isFirst={index === 0}
                        item={item}
                        schooldata={schooldata}
                      />
                    );
                  }
                )}
              {isDesktop && activeData.length > 0 && (
                <>
                  {activeTab === 'studentsData' && (
                    <Paginator
                      key={`students-${activeTab}-${dataVersion}`}
                      currentContentPage={(items) =>
                        handleContentCurrentPage(items)
                      }
                      items={activeData as UsuarioTO[]}
                    />
                  )}
                  {activeTab === 'schoolsData' && (
                    <Paginator
                      key={`school-${activeTab}-${dataVersion}`}
                      currentContentPage={(items) =>
                        handleContentCurrentPage(items)
                      }
                      items={activeData as EscuelaTO[]}
                    />
                  )}
                  {activeTab === 'mySchoolsData' && (
                    <Paginator
                      key={`my-school-${activeTab}-${dataVersion}`}
                      currentContentPage={(items) =>
                        handleContentCurrentPage(items)
                      }
                      items={activeData as PosicionParticipanteMiEscuelaTO[]}
                    />
                  )}
                </>
              )}
              {isMobile &&
                activeData.map(
                  (
                    item:
                      | EscuelaTO
                      | UsuarioTO
                      | PosicionParticipanteMiEscuelaTO,
                    index: number
                  ) => {
                    return (
                      <ItemTdeTable
                        key={`${index}-${Math.random()
                          .toString(36)
                          .substring(2, 15)}`}
                        activeTab={activeTab}
                        index={index}
                        isFirst={index === 0}
                        item={item}
                        schooldata={schooldata}
                      />
                    );
                  }
                )}
            </div>
          </>
        </motion.div>
      )}
    </div>
  );
};

export default RankingTable;
